<template>
  <div class="lease-dist-modal">
    <!-- Title Bar -->
    <div class="title-bar">
      <div class="title-content">
        <h3>Lease End Date Distribution</h3>
      </div>
      <div class="title-buttons">
        <button class="refresh-btn" @click="loadData" title="Refresh Data">
          <i class="fas fa-sync-alt"></i>
        </button>
        <button class="close-btn" @click="$emit('close')">
          <i class="fas fa-times"></i>
        </button>
      </div>
    </div>

    <!-- Chart Container -->
    <div class="chart-body">
      <div v-if="loading" class="loading-indicator">
        <i class="fas fa-spinner fa-spin"></i> Loading lease data...
      </div>
      <div v-if="error" class="error-message">
        <i class="fas fa-exclamation-triangle"></i> {{ error }}
      </div>
      <div ref="chartRef" class="chart-container"></div>

      <!-- Summary row -->
      <div v-if="!loading && !error && summary" class="summary-row">
        <div v-for="item in summary" :key="item.label" class="summary-item">
          <span class="summary-label">{{ item.label }}:</span>
          <span class="summary-value">{{ item.value }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue';
import axios from '@/axiosConfig';
import Plotly from 'plotly.js-dist';

export default {
  name: 'LeaseEndDistribution',
  emits: ['close'],
  setup() {
    const chartRef = ref(null);
    const loading = ref(false);
    const error = ref(null);
    const summary = ref(null);

    const yearColors = {
      2024: '#78909c',
      2025: '#42a5f5',
      2026: '#66bb6a',
      2027: '#ffa726',
    };

    const years = [2024, 2025, 2026, 2027];

    const loadData = async () => {
      loading.value = true;
      error.value = null;
      summary.value = null;

      try {
        const response = await axios.get('/api/lease-end-distribution');
        renderChart(response.data);
      } catch (err) {
        console.error('Error loading lease distribution:', err);
        error.value = 'Failed to load lease data. Please try again.';
      } finally {
        loading.value = false;
      }
    };

    const renderChart = (data) => {
      if (!chartRef.value) return;

      // Group data by year
      const yearData = {};
      years.forEach((y) => {
        yearData[y] = {};
      });

      data.forEach((row) => {
        const d = new Date(row.endDate + 'T00:00:00');
        const y = d.getFullYear();
        if (yearData[y] !== undefined) {
          yearData[y][row.endDate] = row.count;
        }
      });

      // Collect all unique end dates per year, sorted
      const traces = [];

      years.forEach((yr) => {
        const dates = Object.keys(yearData[yr]).sort();
        if (dates.length === 0) return;

        const counts = dates.map((d) => yearData[yr][d]);
        const labels = dates.map((d) => {
          const dt = new Date(d + 'T00:00:00');
          return dt.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          });
        });

        traces.push({
          x: dates,
          y: counts,
          name: String(yr),
          type: 'bar',
          marker: { color: yearColors[yr] || '#9e9e9e' },
          text: counts.map(String),
          textposition: 'outside',
          textfont: { size: 11, color: '#333' },
          hovertemplate:
            '<b>End Date:</b> %{customdata}<br>' +
            '<b>Leases:</b> %{y}<extra></extra>',
          customdata: labels,
        });
      });

      // Build summary
      const yearTotals = years.map((yr) => {
        const total = Object.values(yearData[yr]).reduce((a, b) => a + b, 0);
        return { label: String(yr), value: total };
      });
      const grandTotal = yearTotals.reduce((a, b) => a + b.value, 0);
      yearTotals.push({ label: 'Total', value: grandTotal });
      summary.value = yearTotals;

      const maxCount = Math.max(...data.map((r) => r.count), 1);

      const layout = {
        title: {
          text: 'Lease End Dates by Year',
          font: { size: 16, color: '#333' },
        },
        barmode: 'group',
        xaxis: {
          title: '',
          type: 'category',
          tickangle: -90,
          tickfont: { size: 10 },
          categoryorder: 'category ascending',
        },
        yaxis: {
          title: 'Number of Leases',
          range: [0, maxCount * 1.25],
          automargin: true,
          dtick: Math.max(1, Math.floor(maxCount / 8)),
        },
        legend: {
          orientation: 'h',
          y: -0.25,
          x: 0.5,
          xanchor: 'center',
          font: { size: 12 },
        },
        hoverlabel: {
          bgcolor: 'white',
          bordercolor: '#ccc',
          font: { size: 12 },
        },
        margin: { t: 50, b: 100, l: 60, r: 20 },
        plot_bgcolor: '#fafafa',
        paper_bgcolor: 'white',
      };

      const config = {
        responsive: true,
        displayModeBar: true,
        displaylogo: false,
        modeBarButtonsToRemove: ['lasso2d', 'select2d'],
      };

      Plotly.newPlot(chartRef.value, traces, layout, config);
    };

    onMounted(async () => {
      await nextTick();
      loadData();
    });

    onBeforeUnmount(() => {
      if (chartRef.value) {
        Plotly.purge(chartRef.value);
      }
    });

    return {
      chartRef,
      loading,
      error,
      summary,
      loadData,
    };
  },
};
</script>

<style scoped>
.lease-dist-modal {
  position: relative;
  width: 85vw;
  max-width: 1200px;
  height: 80vh;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
}

.title-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #5c6bc0;
  color: #fff;
  padding: 8px 15px;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  font-size: 1.4em;
  flex-shrink: 0;
}

.title-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.title-content h3 {
  margin: 0;
  color: #fff;
  font-size: 1em;
  font-weight: 600;
}

.title-buttons {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-shrink: 0;
}

.refresh-btn,
.close-btn {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 1px solid #e1dede;
  color: #eee;
  background: #3f51b5;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.refresh-btn {
  margin-right: 30px;
}

.refresh-btn:hover,
.close-btn:hover {
  background: #303f9f;
  color: #fff;
  border-color: #fff;
}

.refresh-btn i,
.close-btn i {
  font-size: 12px;
}

.chart-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 10px;
}

.chart-container {
  flex: 1;
  min-height: 0;
  width: 100%;
}

.loading-indicator {
  text-align: center;
  padding: 40px;
  color: #666;
  font-size: 1.1em;
}

.error-message {
  background: #ffebee;
  color: #c62828;
  padding: 10px 20px;
  border: 1px solid #ef9a9a;
  border-radius: 4px;
  margin: 10px;
}

.summary-row {
  display: flex;
  justify-content: center;
  gap: 30px;
  padding: 8px 20px;
  border-top: 1px solid #e0e0e0;
  background: #fafafa;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  flex-shrink: 0;
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.9em;
}

.summary-label {
  color: #666;
  font-weight: 500;
}

.summary-value {
  font-weight: 600;
  color: #333;
}
</style>
